<template>
  <v-app
    id="inspire"
    :class="accessibilityClasses"
  >
    <!-- Skip Navigation Links -->
    <skip-navigation />

    <!-- WCAG 2.1 AA Compliant: Single top-level banner landmark -->
    <header
      id="main-banner"
      role="banner"
      aria-label="Site header"
    >
      <header-component />
    </header>

    <v-main
      id="main-content"
      app
      fill-height
      fluid
      :class="getContentClases"
      role="main"
    >
      <v-container
        fill-height
        pt-0
        pb-0
        pl-0
        pr-0
        fluid
        aria-label="Main content container"
        role="region"
      >
        <slot />

        <post-dialog
          v-show="dialogPost.slug"
          :post="dialogPost"
          role="dialog"
          aria-modal="true"
          :aria-label="dialogPost.title || 'Post details'"
        />

        <notifications-overlay
          role="region"
          aria-live="polite"
        />

        <menu-component
          role="complementary"
          aria-label="Quick navigation menu"
        />
      </v-container>
    </v-main>

    <footer-component role="contentinfo" />

    <!-- Back to Top Button -->
    <back-to-top-button />
  </v-app>
</template>

<script>
import { mapGetters } from 'vuex';
import HeaderComponent from "@/components/Views/Header.vue";
import FooterComponent from "@/components/Views/Footer.vue";
import PostDialog from "@/components/Views/PostDialog.vue";
import MenuComponent from "@/components/Views/Menu.vue"
import NotificationsOverlay from "@/components/Views/NotificationsOverlay.vue"
import SkipNavigation from "@/components/Views/SkipNavigation.vue"
import BackToTopButton from "@/components/Views/BackToTopButton.vue"
import AccessibilityMixin from "@/mixins/accessibility.mixin.js"

export default {
  name: "AppLayout",
  components: {
    HeaderComponent,
    FooterComponent,
    PostDialog,
    MenuComponent,
    NotificationsOverlay,
    SkipNavigation,
    BackToTopButton,
  },
  mixins: [AccessibilityMixin],
  data() {
    return {
        title: "",
        description: "",
        image: "",
        url: "",
        dialogPost: {},
        isKeyboardUser: false
    }
  },
  computed: {
      ...mapGetters(['getComponentVisibility']),

    getContentClases() {
      let currentClassPage = "";
      if (this.$route.name) {
        currentClassPage = this.$route.name
          .replace("client.", "")
          .replace(/([.])/gi, "-");
      }

      return `mb-0 mt-0 main-wrapper ${currentClassPage}`;
    },

    accessibilityClasses() {
      const classes = [];
      
      // Add high contrast mode if preferred
      if (this.prefersHighContrast()) {
        classes.push('high-contrast-mode');
      }
      
      // Add reduced motion class if preferred
      if (this.prefersReducedMotion()) {
        classes.push('respect-motion-preference');
      }
      
      // Add keyboard navigation active class when needed
      if (this.isKeyboardUser) {
        classes.push('keyboard-navigation-active');
      }
      
      return classes.join(' ');
    },
  },
  watch: {
    $route() {
      this.updateMeta();
      // Announce page changes to screen readers
      this.$nextTick(() => {
        this.announcePageChange();
        // Focus main content after route change
        this.setAccessibleFocus('#main-content');
      });
    },
    getComponentVisibility({ dialog = {} }) {
        this.dialogPost = dialog.post || {};
    }
  },
  beforeMount() {
    this.updateMeta();
  },
  mounted() {
    // Initialize keyboard user detection
    this.initKeyboardDetection();
    
    // Announce page changes to screen readers
    this.announcePageChange();
  },
  methods: {
    updateMeta() {
      this.title = this.$route.meta.title;
      this.description = this.$route.meta.description;
      this.image = this.$route.meta.image;
      this.url = this.$route.path;
    },

    /**
     * Initialize keyboard user detection for enhanced accessibility
     */
    initKeyboardDetection() {
      // Detect if user is using keyboard navigation
      const handleFirstTab = (e) => {
        if (e.key === 'Tab') {
          this.isKeyboardUser = true;
          document.removeEventListener('keydown', handleFirstTab);
          document.addEventListener('mousedown', handleMouseUser);
        }
      };

      const handleMouseUser = () => {
        this.isKeyboardUser = false;
        document.removeEventListener('mousedown', handleMouseUser);
        document.addEventListener('keydown', handleFirstTab);
      };

      document.addEventListener('keydown', handleFirstTab);
    },

    /**
     * Announce page changes to screen readers
     */
    announcePageChange() {
      const pageTitle = this.title || this.$route.name || 'Page';
      this.announceToScreenReader(`Navigated to ${pageTitle}`, 'polite');
    },

    /**
     * Handle escape key for global modal dismissal
     */
    handleEscape() {
      // Close any open dialogs/modals
      if (this.dialogPost.slug) {
        this.dialogPost = {};
        return true; // Stop propagation
      }
      return false; // Continue to other handlers
    }
  },
  metaInfo() {
    return {
      title: this.title,
      meta: [
        // app name
        {
          hid: "applicaton-name",
          vmid: "applicaton-name",
          name: "application-name",
          content: "Name of my application"
        },
        {
          hid: "description",
          vmid: "description",
          name: "description",
          content: this.description
        }, // id to replace intead of create element
        // ...
        // Twitter
        {
          hid: "twitterTitle",
          name: "twitter:title",
          vmid: "twitter:title",
          content: this.title
        },
        // with shorthand
        {
          hid: "twitterDescription",
          vmid: "twitter:description",
          name: "twitter:description",
          content: this.description
        },
        // ...
        // Google+ / Schema.org
        {
          hid: "googleName",
          vmid: "name",
          itemprop: "name",
          content: this.title
        },
        {
          hid: "googleDescription",
          vmid: "description",
          itemprop: "description",
          content: this.description
        },
        // ...
        // Facebook / Open Graph
        {
          hid: "fbAppId",
          vmid: "fbAppId",
          property: "fb:app_id",
          content: "301865970468191"
        },
        {
          hid: "ogTitle",
          vmid: "og:title",
          property: "og:title",
          content: this.title
        },
        {
          hid: "ogDescription",
          vmid: "og:description",
          property: "og:description",
          content: this.description
        },
        // with shorthand
        {
          hid: "ogImage",
          vmid: "og:image",
          property: "og:image",
          content: this.image
        }
        // ...
      ]
    };
  }
};
</script>

<style lang="scss">
@import '~/_variables.scss';

    .main-wrapper {
        background-color: #424242;
    }

    .main-wrapper.profile-overview,
    .main-wrapper.profile-downloads,
    .main-wrapper.profile-change-password,
    .main-wrapper.profile-communities,
    .main-wrapper.profile-transactions,
    .main-wrapper.profile-settings,
    .main-wrapper.landing,
    .main-wrapper.gift,
    .main-wrapper.gift-points,
    .main-wrapper.notes,
    .main-wrapper.events,
    .main-wrapper.guides,
    .main-wrapper.checkout,
    .main-wrapper.checkout-completed,
    .main-wrapper.checkout-cancelled,
    .main-wrapper.complete-oauth-profile,
    .main-wrapper.about,
    .main-wrapper.tc,
    .main-wrapper.tc-conditions,
    .main-wrapper.tc-policy,
    .main-wrapper.shop-popular,
    .main-wrapper.shop-pet,
    .main-wrapper.shop-limited,
    .main-wrapper.shop-char,
    .main-wrapper.shop {
        padding: 0 !important;
    }

    .main-wrapper.faq,
    .main-wrapper.shop,
    .main-wrapper.shop-popular,
    .main-wrapper.shop-pet,
    .main-wrapper.shop-limited,
    .main-wrapper.shop-char,
    .main-wrapper.about,
    .main-wrapper.tc,
    .main-wrapper.tc-conditions,
    .main-wrapper.tc-policy {
        background-color: $primary;
    }

    .link-accent {
        text-decoration: overline !important;
        color: $accent!important;
    }

    .fb_customer_chat_bubble_animated_no_badge {
        bottom: 4.8rem!important;
    }

    .page-loading {
        padding: 0;
    }
</style>
